import { makeVar, ReactiveVar } from "@apollo/client";

export const getCleanValueForStorage = (value: unknown): string => {
  return typeof value === "string" ? value : JSON.stringify(value);
};

const isJsonString = (str: string): boolean => {
  // Check if string looks like it was JSON.stringify'd...

  // Is a JSON string (quoted)
  if (/^"[^"]*"$/.test(str)) return true;

  // Is a JSON object or array
  if (/^{.*?}$/.test(str) || /^\[.*?\]$/.test(str)) return true;

  // Is a JSON primitive
  if (/^(true|false|null)$/.test(str)) return true;

  // Is a JSON number
  if (/^-?\d+(?:\.\d+)?$/.test(str)) return true;

  return false;
};

export const makeVarPersisted = <T>(initialValue: T, storageName: string): ReactiveVar<T> => {
  let value = initialValue;

  const previousValue = localStorage.getItem(storageName);

  if (previousValue !== null) {
    // Detect if the stored value was JSON.stringify'd (old format) or clean (new format)
    value = isJsonString(previousValue) ? JSON.parse(previousValue) : (previousValue as T);
  }

  const reactiveVariable = makeVar<T>(value);

  const onNext = (newValue: T | undefined) => {
    // Try to add the value to local storage
    if (newValue === undefined) {
      localStorage.removeItem(storageName);
    } else {
      localStorage.setItem(storageName, getCleanValueForStorage(newValue));
    }

    // Re-register for the next change
    reactiveVariable.onNextChange(onNext);
  };

  // Register for the first change
  reactiveVariable.onNextChange(onNext);

  return reactiveVariable;
};
